import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { YButton } from '../../../ui/YButton';
// V2 enabled: force V2 on this page
import Lightbox from '../../../components/common/Lightbox';
import { CachedImage } from '../../../components/common/CachedImage';
import PhotoEngagementPanel from '../../../components/common/PhotoEngagementPanel';
import { FaUsers, FaCrown, FaTrash, FaArrowLeft, FaEnvelope, FaMap, FaTree, FaSignOutAlt, FaSync, FaInfoCircle, FaCalendarAlt, FaFlag, FaSearch, FaUserPlus, FaChartPie } from 'react-icons/fa';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import api from '../../../utils/api';
import {
  clubStatsKey,
  getCachedClubHarvests,
  getCachedStats,
  isCachedClubHarvestsFresh,
  isCachedStatsFresh,
  normalizeHuntsList,
  setCachedClubHarvests,
  setCachedStats,
} from '../../../utils/statsCache';
import { fetchClub, getCachedClub, setCachedClub } from '../utils/clubsCache';
import { fetchClubSeasons, huntsQuery, seasonQuery } from '../services/clubSeasonsService';
import { initialSeason } from '../utils/clubSeasonPicker';
import { ClubSeasonPicker } from './ClubSeasonPicker';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import NavigationService from '../../../services/navigationService';
import { messagingService } from '../../../features/messaging/services/messagingService';
import { useToast } from '../../../components/ui/ToastProvider';
import { ClubTabsNav } from './ClubTabsNav';
import { ClubTileHub, ClubHubBackBar } from './ClubTileHub';
import { resolveClubDetailSectionMode, shouldShowClubNameTopbar } from '../clubSections';
import { useIsMobileMd } from '../../../hooks/useMatchMedia';
import { useClubBoardUnread } from '../hooks/useClubBoardUnread';
import { getHuntersInWoods, collectProfileIds, matchOfficerToMember, profilesFromClub } from '../utils/clubUtils';
import { huntZoneOverviewRows } from '../utils/huntZoneOverview';
import { useMemberProfiles } from '../hooks/useMemberProfiles';
import ClubRulesPage from './ClubRulesPage';
import ClubCalendarPage from './ClubCalendarPage';
import ClubBoardPage from './ClubBoardPage';
import ClubThreadPage from './ClubThreadPage';
import ClubAdminPage from './ClubAdminPage';
import { UnifiedMapPage } from '../../map/components/UnifiedMapPage';
import { GiDeerTrack } from 'react-icons/gi';
// Calendar moved to dedicated page /clubs/:id/calendar
import { YInput } from '../../../ui/YInput';
import { YTable } from '../../../ui/YTable';
import { AddMemberAccordion } from './AddMemberAccordion';
import { InviteMemberModal } from './InviteMemberModal';
import { onHuntCompleted, consumePendingHuntCompleted } from '../../../utils/huntEvents';
import { toLocalYMD } from '../../../utils/dateUtils';
import { SignOutModal } from '../../map/components/SignOutModal';
import { HuntSignOutService } from '../../map/services/huntSignOutService';
import { notifyInTheStandChanged } from '../../../hooks/useInTheStandCount';
import {
  YDeerSightingsTimelineChart,
  useDeerSightingsTimelineBrush,
  StatsPie
} from '../../../ui/charts';
import { ClubMemberActivityChart } from './ClubMemberActivityChart';
import { ClubFeedersSummaryPanel } from './ClubFeedersSummaryPanel';
import { ClubSharedPlansPanel } from './ClubSharedPlansPanel';
import { searchItems } from '../../../utils/searchUtils';
import { getCentroidFromBoundaries } from '../../map/utils/geometryUtils';
import { weatherService } from '../../map/services/weatherService';
import { clubMapTabPath, clubMapDrawBoundaryPath } from '../utils/clubMapTabRoutes';
import { DeerSightingsHeatmap } from '../../statistics/components/DeerSightingsHeatmap';
import {
  clubHasMapBoundary,
  formatClubDetailShellMeta,
  getClubInitials,
} from '../clubsDisplayUtils';
import {
  isClubJoinGateActive,
  clubJoinGateRulesPath,
  isClubRulesPath,
} from '../utils/clubJoinGateUtils';
import './club-detail-shell.css';
import './clubDetailFieldEdition.css';
import './clubOverviewFieldEdition.css';
import './clubMembersFieldEdition.css';
import './clubStatsFieldEdition.css';

// Compact inline list inside Club Details card (V2)
const MembersInlineListV2 = React.memo(function MembersInlineListV2({ club, onMessage, onRemove, initialCount = 5, profilesById: profilesByIdProp, searchQuery = '' }) {
  const members = useMemo(
    () => (Array.isArray(club?.members) ? club.members : []),
    [club?.members]
  );
  const fallbackProfilesById = useMemberProfiles(profilesByIdProp ? [] : members);
  const profilesById = profilesByIdProp || fallbackProfilesById;
  const filteredMembers = useMemo(() => {
    const q = (searchQuery || '').trim();
    if (!q) return members;
    return searchItems(members, q, {
      fields: [
        (m) => m.username,
        (m) => m.email,
        (m) => {
          const p = profilesById[m.id] || {};
          return [p.first_name, p.last_name].filter(Boolean).join(' ');
        },
      ],
    });
  }, [members, searchQuery, profilesById]);
  const [visible, setVisible] = useState(initialCount);
  useEffect(() => {
    setVisible(initialCount);
  }, [searchQuery, initialCount, club?.id]);
  const showMore = filteredMembers.length > visible;
  const items = filteredMembers.slice(0, visible);
  const navigate = useNavigate();
  if (members.length === 0) {
    return (
      <div className="club-members-empty" role="status" data-testid="club-members-empty">
        <div className="club-members-empty__icon" aria-hidden>
          <FaUsers />
        </div>
        <h6 className="club-members-empty__title">No members on the roster</h6>
        <p className="club-members-empty__hint mb-0">
          {club?.is_president
            ? 'Search Yona users above to send an invite.'
            : 'Your club president can invite members.'}
        </p>
      </div>
    );
  }
  if (filteredMembers.length === 0) {
    return (
      <div className="club-members-roster" data-testid="club-members-roster">
        <div className="club-members-roster__band">
          <span className="club-members-roster__band-title">Roster</span>
          <span className="club-members-roster__band-count" data-testid="club-members-roster-count">
            0 members
          </span>
        </div>
        <div className="club-members-no-match" role="status" data-testid="club-members-no-match">
          <div className="club-members-empty__icon" aria-hidden>
            <FaSearch />
          </div>
          <h6 className="club-members-empty__title">No members match your search</h6>
          <p className="club-members-empty__hint mb-0">Try a different name, username, or email.</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="club-members-roster" data-testid="club-members-roster">
        <div className="club-members-roster__band">
          <span className="club-members-roster__band-title">Roster</span>
          <span className="club-members-roster__band-count" data-testid="club-members-roster-count">
            {filteredMembers.length} member{filteredMembers.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="club-members-list" role="list" data-testid="club-members-list">
          {items.map((member) => {
            const prof = profilesById[member.id] || {};
            const fullName = [prof.first_name, prof.last_name].filter(Boolean).join(' ');
            const displayName = fullName || member.username || '';
            const initials = (fullName || member.username || '')
              .split(' ')
              .map((s) => s.charAt(0).toUpperCase())
              .slice(0, 2)
              .join('');
            const isPresident = (club.president?.id || club.president) === member.id;
            return (
              <div key={member.id} className="club-members-row" role="listitem">
                <div className="club-members-row__main min-w-0">
                  <button
                    type="button"
                    className="club-members-profile"
                    onClick={() => navigate(`/users/${member.id}`)}
                    aria-label={`View ${displayName}'s profile`}
                  >
                    {prof.photo_url ? (
                      <CachedImage src={prof.photo_url} alt={member.username || displayName || 'Member'} className="nav-avatar" />
                    ) : (
                      <div className="nav-avatar-fallback" aria-hidden>
                        {initials}
                      </div>
                    )}
                    <div className="club-members-profile__text min-w-0">
                      <div className="club-members-profile__name-row">
                        <span className="club-members-row__name">{displayName}</span>
                        {isPresident && (
                          <span className="club-members-badge club-members-badge--president">
                            <FaCrown className="me-1" aria-hidden />
                            President
                          </span>
                        )}
                      </div>
                      {member.username ? (
                        <div className="club-members-row__username text-muted small">@{member.username}</div>
                      ) : null}
                    </div>
                  </button>
                </div>
                <div className="club-members-row__actions">
                  <YButton
                    variant="ghost"
                    size="sm"
                    className="club-members-icon-btn"
                    onClick={() => onMessage && onMessage(member)}
                    title={`Message ${member.username || displayName}`}
                    aria-label={`Message ${member.username || displayName}`}
                  >
                    <FaEnvelope />
                  </YButton>
                  {club.is_president && !isPresident && (
                    <YButton
                      variant="ghost"
                      size="sm"
                      className="club-members-icon-btn"
                      onClick={() => onRemove && onRemove(member.id)}
                      title={`Remove ${member.username || displayName}`}
                      aria-label={`Remove ${member.username || displayName}`}
                    >
                      <FaTrash />
                    </YButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showMore && (
        <div className="club-members-show-more">
          <YButton variant="secondary" size="sm" onClick={() => setVisible((v) => Math.min(filteredMembers.length, v + 5))}>
            Show more
          </YButton>
        </div>
      )}
    </>
  );
});

const InTheWoodsPanel = React.memo(function InTheWoodsPanel({ isV2, huntersInWoods, currentUserId, clubIsCreator, onEndHunt, onRequestEndHunt, canViewMap, canDrawBoundary, onViewMap, onDrawBoundary, clubWeather, clubWeatherLoading, clubWeatherError, showClubWeather, clubStats, statsLoading }) {
  const Body = (
        <div>
          {showClubWeather && (
            <div className="club-inwoods-weather">
              <div className="club-detail-section__subhead">Current weather</div>
              {clubWeatherLoading && !clubWeather ? (
                <div className="d-flex align-items-center text-muted small">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading weather…
                </div>
              ) : clubWeatherError && !clubWeather ? (
                <div className="text-danger small">{clubWeatherError}</div>
              ) : clubWeather ? (
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <span style={{ fontSize: '1.25rem' }}>{weatherService.getWeatherIcon(clubWeather.icon)}</span>
                  <span className="fw-semibold">{clubWeather.temperature}°</span>
                  <span className="text-muted small">{weatherService.getWeatherDescription(clubWeather.description)}</span>
                  <span className="text-muted small">{clubWeather.wind_speed} mph {weatherService.getWindDirection(clubWeather.wind_direction)}</span>
                </div>
              ) : null}
            </div>
          )}
          {huntersInWoods.length === 0 ? (
            <div className="club-inwoods-empty">
              <div className="club-inwoods-empty__icon" aria-hidden>
                <FaTree />
              </div>
              <h6 className="club-inwoods-empty__title">Nobody in the woods</h6>
              <p className="club-inwoods-empty__hint mb-0">
                No members are currently signed out
              </p>
            </div>
          ) : (
            <div className="mb-1">
              {huntersInWoods.map((hunter, index) => (
                <div key={index} className="club-inwoods-row">
                  <div className="min-w-0">
                    <div className="club-inwoods-row__name">{hunter.userName}</div>
                    <div className="club-inwoods-row__loc">at {hunter.locationName}</div>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <span className="club-inwoods-badge">{hunter.timePeriod}</span>
                    {parseInt(hunter.userId) === parseInt(currentUserId) ? (
                      isV2 ? (
                        <YButton size="sm" variant="secondary" onClick={() => onEndHunt(hunter)} className="d-inline-flex align-items-center"><FaSignOutAlt className="me-1" /> End Hunt</YButton>
                      ) : (
                        <Button variant="outline-warning" size="sm" onClick={() => onEndHunt(hunter)} className="d-flex align-items-center">
                          <FaSignOutAlt className="me-1" />
                          End Hunt
                        </Button>
                      )
                    ) : clubIsCreator ? (
                      isV2 ? (
                        <YButton size="sm" variant="secondary" onClick={() => onRequestEndHunt(hunter)} className="d-inline-flex align-items-center"><FaEnvelope className="me-1" /> Request End Hunt</YButton>
                      ) : (
                        <Button variant="outline-info" size="sm" onClick={() => onRequestEndHunt(hunter)} className="d-flex align-items-center">
                          <FaEnvelope className="me-1" />
                          Request End Hunt
                        </Button>
                      )
                    ) : (
                      isV2 ? (
                        <YButton size="sm" variant="secondary" disabled className="d-inline-flex align-items-center" title="You can only end your own hunts"><FaEnvelope className="me-1" /> Not Your Hunt</YButton>
                      ) : (
                        <Button variant="outline-secondary" size="sm" disabled title="You can only end your own hunts" className="d-flex align-items-center">
                          <FaEnvelope className="me-1" />
                          Not Your Hunt
                        </Button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(canViewMap || canDrawBoundary) && (
            <div className="club-inwoods-actions">
              {canViewMap ? (
              (isV2 ? (
                <YButton variant="secondary" size="sm" onClick={onViewMap} className="d-inline-flex align-items-center mx-auto">
                  <FaMap className="me-1" /> View Full Map
                </YButton>
              ) : (
                <Button variant="outline-primary" size="sm" onClick={onViewMap} className="d-flex align-items-center mx-auto">
                  <FaMap className="me-1" />
                  View Full Map
                </Button>
              ))
              ) : null}
              {!canViewMap && canDrawBoundary ? (
              (isV2 ? (
                <YButton variant="secondary" size="sm" onClick={onDrawBoundary} className="d-inline-flex align-items-center mx-auto">
                  <FaMap className="me-1" /> Draw Club Boundary
                </YButton>
              ) : (
                <Button variant="outline-primary" size="sm" onClick={onDrawBoundary} className="d-flex align-items-center mx-auto">
                  <FaMap className="me-1" />
                  Draw Club Boundary
                </Button>
              ))
              ) : null}
            </div>
          )}

          <ClubMemberActivityChart
            clubStats={clubStats}
            statsLoading={statsLoading}
            variant="embedded"
          />
        </div>
  );
  return isV2 ? (
    <div className="club-detail-section club-detail-section--inwoods" data-testid="club-detail-inwoods-panel">
      <div className="club-inwoods-band">
        <span className="club-inwoods-band__title">In the woods</span>
        <span className="club-inwoods-band__count" data-testid="club-inwoods-count">
          {huntersInWoods.length} out
        </span>
      </div>
      <div className="club-inwoods-body">{Body}</div>
    </div>
  ) : (
    <Card>
      <Card.Header>
        <h5 className="mb-0 d-flex align-items-center">
          <FaTree className="me-2" aria-hidden />
          In The Woods
        </h5>
      </Card.Header>
      <Card.Body>{Body}</Card.Body>
    </Card>
  );
});

const DeerLogTable = React.memo(function DeerLogTable({ isV2, harvests, onImageClick, limit, offset, count, onPrev, onNext, searchTerm, onSearchChange }) {
  const TableBody = harvests && harvests.length > 0 ? (
    isV2 ? (
      <YTable>
        <thead>
          <tr>
            <th>Date</th>
            <th>Member</th>
            <th>Location</th>
            <th>Details</th>
            <th>Photo</th>
          </tr>
        </thead>
        <tbody>
          {harvests.map((h, idx) => {
            const name = (h.hunter_first_name && h.hunter_last_name)
              ? `${h.hunter_first_name} ${h.hunter_last_name}`
              : h.hunter_username || 'Member';
            const sex = h.deer_type === 'buck' ? 'Buck' : 'Doe';
            const subdetails = [
              h.weight ? `${h.weight} lbs` : null,
              (h.deer_type === 'buck' && h.points != null) ? `${h.points} pts` : null
            ].filter(Boolean).join(' · ');
            return (
              <tr key={`${h.harvest_id}-${idx}`}>
                <td>{(() => { const { formatLocalDate } = require('../../../utils/dateUtils'); return formatLocalDate(h.date, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US'); })()}</td>
                <td>{name}</td>
                <td>{h.location || '-'}</td>
                <td>
                  <div>{sex}</div>
                  <div className="text-muted small">{subdetails || '-'}</div>
                </td>
                <td style={{ width: 80 }}>
                  {(h.photo_thumb_url || h.photo_url) ? (
                    <CachedImage
                      src={h.photo_thumb_url || h.photo_url}
                      alt="Harvest"
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }}
                      onClick={() => onImageClick(idx)}
                    />
                  ) : (
                    <span className="text-muted small">No Photo</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </YTable>
    ) : (
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Date</th>
              <th>Member</th>
              <th>Location</th>
              <th>Details</th>
              <th>Photo</th>
            </tr>
          </thead>
          <tbody>
            {harvests.map((h, idx) => {
              const name = (h.hunter_first_name && h.hunter_last_name)
                ? `${h.hunter_first_name} ${h.hunter_last_name}`
                : h.hunter_username || 'Member';
              const sex = h.deer_type === 'buck' ? 'Buck' : 'Doe';
              const subdetails = [
                h.weight ? `${h.weight} lbs` : null,
                (h.deer_type === 'buck' && h.points != null) ? `${h.points} pts` : null
              ].filter(Boolean).join(' · ');
              return (
                <tr key={`${h.harvest_id}-${idx}`}>
                  <td>{(() => { const { formatLocalDate } = require('../../../utils/dateUtils'); return formatLocalDate(h.date, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US'); })()}</td>
                  <td>{name}</td>
                  <td>{h.location || '-'}</td>
                  <td>
                    <div>{sex}</div>
                    <div className="text-muted small">{subdetails || '-'}</div>
                  </td>
                  <td style={{ width: 80 }}>
                    {(h.photo_thumb_url || h.photo_url) ? (
                      <CachedImage
                        src={h.photo_thumb_url || h.photo_url}
                        alt="Harvest"
                        style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }}
                        onClick={() => onImageClick(idx)}
                      />
                    ) : (
                      <span className="text-muted small">No Photo</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )
  ) : (
    <div className="text-muted">No recent harvests yet.</div>
  );
  const currentPage = Math.max(1, Math.floor(offset / Math.max(1, limit)) + 1);
  const totalPages = Math.max(1, Math.ceil(Math.max(0, count) / Math.max(1, limit)));
  const pageCount = Array.isArray(harvests) ? harvests.length : 0;
  const startIdx = count > 0 ? Math.min(count, offset + 1) : 0;
  const endIdx = count > 0 ? Math.min(count, offset + pageCount) : 0;

  const Footer = (
    <div className="d-flex justify-content-between align-items-center mt-2">
      <small className="text-muted">{`Page ${currentPage} of ${totalPages} · Showing ${startIdx}${endIdx > startIdx ? `–${endIdx}` : ''} of ${count}`}</small>
      <div className="d-flex gap-2">
        {isV2 ? (
          <>
            <YButton variant="secondary" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrev && onPrev(); }} disabled={offset <= 0}>Previous</YButton>
            <YButton variant="secondary" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext && onNext(); }} disabled={(offset + limit) >= count}>Next</YButton>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrev && onPrev(); }} disabled={offset <= 0}>Previous</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext && onNext(); }} disabled={(offset + limit) >= count}>Next</button>
          </>
        )}
      </div>
    </div>
  );

  return isV2 ? (
    <div className="club-detail-section club-log-panel mb-0" data-testid="club-deer-log">
      <div className="club-detail-section__kicker">
        <GiDeerTrack aria-hidden />
        <span>Deer log</span>
      </div>
      <p className="club-detail-section__subhead mb-3">Harvests recorded for this club.</p>
      <div className="club-log-panel__toolbar mb-3">
        <div className="club-members-search club-log-panel__search">
          <FaSearch className="club-members-search__icon" aria-hidden />
          <YInput
            type="search"
            className="club-members-search__input"
            placeholder="Name, location, type…"
            aria-label="Search deer log"
            value={searchTerm}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          />
        </div>
      </div>
      <div className="club-stats-panel__table-wrap">{TableBody}</div>
      <div className="club-log-panel__footer pt-3 mt-3">
        {Footer}
      </div>
    </div>
  ) : (
    <Card>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between gap-2">
          <h5 className="mb-0">Deer Log</h5>
          <InputGroup size="sm" style={{ maxWidth: 260 }}>
            <InputGroup.Text>Search</InputGroup.Text>
            <Form.Control
              placeholder="Name, location, type…"
              value={searchTerm}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              style={{ fontFamily: 'inherit' }}
            />
          </InputGroup>
        </div>
      </Card.Header>
      <Card.Body>
        {TableBody}
        {Footer}
      </Card.Body>
    </Card>
  );
});

const ClubHuntLog = React.memo(function ClubHuntLog({ isV2, hunts, count, limit, offset, loading, onPrev, onNext, searchTerm, onSearchChange, isSearchingAll }) {
  const currentPage = Math.max(1, Math.floor(offset / Math.max(1, limit)) + 1);
  const totalPages = Math.max(1, Math.ceil(Math.max(0, count) / Math.max(1, limit)));
  const pageCount = Array.isArray(hunts) ? hunts.length : 0;
  const startIdx = count > 0 ? Math.min(count, offset + 1) : 0;
  const endIdx = count > 0 ? Math.min(count, offset + pageCount) : 0;

  const huntLogFooter = (
    <div className="d-flex justify-content-between align-items-center mt-2">
      <small className="text-muted">{`Page ${currentPage} of ${totalPages} · Showing ${startIdx}${endIdx > startIdx ? `–${endIdx}` : ''} of ${count}`}</small>
      <div className="d-flex gap-2">
        {isV2 ? (
          <>
            <YButton variant="secondary" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrev && onPrev(); }} disabled={offset <= 0}>Previous</YButton>
            <YButton variant="secondary" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext && onNext(); }} disabled={(offset + limit) >= count}>Next</YButton>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrev && onPrev(); }} disabled={offset <= 0}>Previous</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext && onNext(); }} disabled={(offset + limit) >= count}>Next</button>
          </>
        )}
      </div>
    </div>
  );

  const huntLogLoadingOverlay = loading ? (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-busy="true">
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 12,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'color-mix(in srgb, var(--color-surface) 88%, transparent)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          padding: '4px 8px',
        }}
      >
        <Spinner animation="border" size="sm" />
        <span className="small text-muted">Loading…</span>
      </div>
    </div>
  ) : null;

  const huntLogTableV2 = (
    <YTable>
      <thead>
        <tr>
          <th>Date</th>
          <th>Member</th>
          <th>Location</th>
          <th>Deer Seen</th>
          <th>Harvest</th>
        </tr>
      </thead>
      <tbody>
        {(hunts && hunts.length > 0) ? hunts.map((h) => (
          <tr key={h.id}>
            <td>{(function(){
              try {
                // eslint-disable-next-line global-require
                const { formatLocalDate } = require('../../../utils/dateUtils');
                return formatLocalDate(h.date, { month: 'short', day: 'numeric' }, 'en-US');
              } catch(_) {
                return new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              }
            })()}</td>
            <td>{h.user?.username || 'Member'}</td>
            <td>{h.location || '-'}</td>
            <td>{h.total_deer_seen}</td>
            <td className="text-center" style={{ width: 80 }}>{h.harvest ? '✓' : ''}</td>
          </tr>
        )) : (
          <tr>
            <td colSpan={5} className="text-muted">No recent hunts yet.</td>
          </tr>
        )}
      </tbody>
    </YTable>
  );

  const Body = (
    <div style={{ position: 'relative' }}>
      {isV2 ? (
        huntLogTableV2
      ) : (
        <div className="table-responsive" style={{ minHeight: `${48 + (limit * 44)}px` }}>
          <div className="d-flex justify-content-end mb-2">
            <InputGroup size="sm" style={{ maxWidth: 260 }}>
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                placeholder="Member, date, location…"
                value={searchTerm}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              />
            </InputGroup>
          </div>
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Member</th>
                <th>Location</th>
                <th>Deer Seen</th>
                <th>Harvest</th>
              </tr>
            </thead>
            <tbody>
              {(hunts && hunts.length > 0) ? hunts.map((h) => (
                <tr key={h.id}>
                  <td>{(() => { const { formatLocalDate } = require('../../../utils/dateUtils'); return formatLocalDate(h.date, { month: 'short', day: 'numeric' }, 'en-US'); })()}</td>
                  <td>{h.user?.username || 'Member'}</td>
                  <td>{h.location || '-'}</td>
                  <td>{h.total_deer_seen}</td>
                  <td className="text-center" style={{ width: 80 }}>{h.harvest ? '✓' : ''}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={5} className="text-muted">No recent hunts yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!isV2 && huntLogFooter}
      {!isV2 && huntLogLoadingOverlay}
    </div>
  );

  return (
    <Col lg={12} className="mb-4">
      {isV2 ? (
        <div className="club-detail-section club-log-panel mb-0" data-testid="club-hunt-log">
          <div className="club-detail-section__kicker">
            <GiDeerTrack aria-hidden />
            <span>Hunt log</span>
          </div>
          <p className="club-detail-section__subhead mb-3">Member hunts and sightings for this club.</p>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="min-w-0 flex-grow-1" style={{ minHeight: 24 }}>
              {isSearchingAll ? (
                <div className="d-inline-flex align-items-center gap-2 text-muted small">
                  <Spinner animation="border" size="sm" />
                  <span>Loading all hunts…</span>
                </div>
              ) : null}
            </div>
            <div className="club-members-search club-log-panel__search flex-shrink-0">
              <FaSearch className="club-members-search__icon" aria-hidden />
              <YInput
                type="search"
                className="club-members-search__input"
                placeholder="Member, date, location…"
                aria-label="Search hunt log"
                value={searchTerm}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              />
            </div>
          </div>
          <div
            className="club-stats-panel__table-wrap position-relative"
            style={{ minHeight: `${48 + (limit * 44)}px` }}
          >
            {huntLogTableV2}
            {huntLogLoadingOverlay}
          </div>
          <div className="club-log-panel__footer pt-3 mt-3">
            {huntLogFooter}
          </div>
        </div>
      ) : (
        <Card>
          <Card.Header>
            <h5 className="mb-0 d-flex align-items-center"><GiDeerTrack className="me-2" />Hunt Log</h5>
          </Card.Header>
          <Card.Body>{Body}</Card.Body>
        </Card>
      )}
    </Col>
  );
});

const ClubStatsSeasonTiles = React.memo(function ClubStatsSeasonTiles({
  clubStats,
  statsLoading,
  onRefresh,
  seasons,
  season,
  onSeasonChange,
}) {
  const totalSightings = useMemo(() => {
    if (!clubStats) return null;
    if (clubStats.total_deer_seen != null) return clubStats.total_deer_seen;
    const d = clubStats.deer_sightings || {};
    return (d.does || 0) + (d.bucks || 0) + (d.fawns || 0);
  }, [clubStats]);

  const buckHarvest = clubStats?.harvests?.bucks ?? 0;
  const doeHarvest = clubStats?.harvests?.does ?? 0;

  const avgPerHunt = useMemo(() => {
    if (!clubStats || !clubStats.total_hunts || totalSightings == null) return null;
    return (totalSightings / clubStats.total_hunts).toFixed(1);
  }, [clubStats, totalSightings]);

  const showLoadingPlaceholder = statsLoading && !clubStats;
  const huntsVal = showLoadingPlaceholder ? '…' : clubStats != null ? clubStats.total_hunts : '—';
  const sightingsVal = showLoadingPlaceholder ? '…' : totalSightings != null ? totalSightings : '—';
  const buckHVal = showLoadingPlaceholder ? '…' : clubStats != null ? buckHarvest : '—';
  const doeHVal = showLoadingPlaceholder ? '…' : clubStats != null ? doeHarvest : '—';
  const avgVal = showLoadingPlaceholder ? '…' : clubStats != null ? (avgPerHunt != null ? avgPerHunt : '0.0') : '—';

  return (
    <div className="club-stats-season-tiles-wrap mb-4" data-testid="club-stats-season-tiles">
      <div className="d-flex align-items-center mb-2 gap-2 flex-wrap">
        <ClubSeasonPicker
          seasons={seasons}
          value={season}
          onChange={onSeasonChange}
          disabled={statsLoading}
        />
        {onRefresh ? (
          <YButton
            className="ms-auto"
            variant="ghost"
            size="sm"
            aria-label="Refresh statistics"
            title="Refresh statistics"
            onClick={onRefresh}
            disabled={statsLoading}
          >
            <FaSync />
          </YButton>
        ) : null}
      </div>
      {showLoadingPlaceholder ? (
        <div className="club-detail-section text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted small">Loading statistics…</span>
        </div>
      ) : (
        <Row className="row-cols-2 row-cols-md-3 row-cols-lg-5 g-3">
          <Col>
            <div className="club-stats-season-card h-100" role="group" aria-label="Total hunts">
              <div className="club-stats-season-card__label">Total hunts</div>
              <div className="club-stats-season-card__value">{huntsVal}</div>
            </div>
          </Col>
          <Col>
            <div className="club-stats-season-card h-100" role="group" aria-label="Sightings">
              <div className="club-stats-season-card__label">Sightings</div>
              <div className="club-stats-season-card__value">{sightingsVal}</div>
            </div>
          </Col>
          <Col>
            <div className="club-stats-season-card h-100" role="group" aria-label="Bucks harvested">
              <div className="club-stats-season-card__label">Bucks harvested</div>
              <div className="club-stats-season-card__value">{buckHVal}</div>
            </div>
          </Col>
          <Col>
            <div className="club-stats-season-card h-100" role="group" aria-label="Does harvested">
              <div className="club-stats-season-card__label">Does harvested</div>
              <div className="club-stats-season-card__value">{doeHVal}</div>
            </div>
          </Col>
          <Col>
            <div className="club-stats-season-card h-100" role="group" aria-label="Average deer sightings per hunt">
              <div className="club-stats-season-card__label">Avg / hunt</div>
              <div className="club-stats-season-card__value">{avgVal}</div>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
});

const ClubStatsPanel = React.memo(function ClubStatsPanel({ clubStats, statsLoading, club }) {
  const isV2 = true;
  const chartData = useMemo(() => (Array.isArray(clubStats?.chart_data) ? clubStats.chart_data : []), [clubStats]);
  const {
    selection: deerBrushSelection,
    onChange: handleDeerBrushChange,
    reset: resetDeerBrush,
    summary: deerBrushSummary,
    version: deerBrushVersion
  } = useDeerSightingsTimelineBrush(chartData);

  const timeBreakdown = useMemo(
    () => (Array.isArray(clubStats?.time_period_breakdown) ? clubStats.time_period_breakdown : []),
    [clubStats]
  );
  const weatherBreakdown = useMemo(
    () => (Array.isArray(clubStats?.weather_breakdown) ? clubStats.weather_breakdown : []),
    [clubStats]
  );
  const tempBreakdown = useMemo(
    () => (Array.isArray(clubStats?.temperature_breakdown) ? clubStats.temperature_breakdown : []),
    [clubStats]
  );
  const moonBreakdown = useMemo(
    () => (Array.isArray(clubStats?.moon_phase_breakdown) ? clubStats.moon_phase_breakdown : []),
    [clubStats]
  );

  const deerTimelineBody = chartData.length > 0 ? (
    <YDeerSightingsTimelineChart
      data={chartData}
      height={260}
      animate={!statsLoading && !!clubStats}
      brush={{
        enabled: true,
        selection: deerBrushSelection,
        onChange: handleDeerBrushChange,
        version: deerBrushVersion
      }}
      brushSummary={deerBrushSummary}
      brushActions={
        deerBrushSummary ? (
          <YButton
            variant="ghost"
            size="sm"
            aria-label="Reset zoom"
            title="Reset zoom"
            onClick={resetDeerBrush}
          >
            <FaSync />
          </YButton>
        ) : null
      }
      gradientInstanceId="club-deer-sightings"
    />
  ) : (
    <div className="text-center py-4">
      <p className="text-muted mb-0">
        <GiDeerTrack className="me-2" aria-hidden />
        No hunt data available yet
      </p>
    </div>
  );

  const chartContent = deerTimelineBody;

  return (
    <Col lg={12} className="mb-4">
      {isV2 ? (
        <div className="club-stats-panel" data-testid="club-stats-panel">
          {statsLoading && !clubStats ? (
            <div className="club-detail-section text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted small">Loading statistics…</span>
            </div>
          ) : clubStats ? (
            <>
              <div className="club-detail-section mb-4">
                <div className="club-detail-section__kicker">
                  <GiDeerTrack aria-hidden />
                  <span>Deer sightings over time</span>
                </div>
                <div className="club-stats-panel__chart-well">{chartContent}</div>
              </div>

              <ClubMemberActivityChart clubStats={clubStats} statsLoading={statsLoading} />

              {club?.id && (
                <div className="club-detail-section mb-4">
                  <div className="club-detail-section__kicker">
                    <FaMap aria-hidden />
                    <span>Deer sightings map</span>
                  </div>
                  <DeerSightingsHeatmap
                    mode="club"
                    clubId={club.id}
                    boundaryBbox={Array.isArray(club?.map?.boundary_bbox) && club.map.boundary_bbox.length === 4 ? club.map.boundary_bbox : undefined}
                  />
                </div>
              )}

              <div className="club-detail-section mb-4">
                <div className="club-detail-section__kicker">
                  <FaChartPie aria-hidden />
                  <span>Breakdowns</span>
                </div>
                <p className="club-detail-section__subhead mb-3">Deer seen by situation</p>
                <Row className="g-4 mb-0">
                  <Col md={6}>
                    <div className="club-stats-panel__chart-well h-100">
                      <StatsPie title="Deer Seen by Time of Day" data={timeBreakdown} nameKey="time_period" valueKey="deer_seen" loading={statsLoading} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="club-stats-panel__chart-well h-100">
                      <StatsPie title="Deer Seen by Weather" data={weatherBreakdown} nameKey="weather" valueKey="deer_seen" loading={statsLoading} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="club-stats-panel__chart-well h-100">
                      <StatsPie title="Deer Seen by Temperature" data={tempBreakdown.map((t) => ({ name: t.label || t.bucket, deer_seen: t.deer_seen }))} nameKey="name" valueKey="deer_seen" loading={statsLoading} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="club-stats-panel__chart-well h-100">
                      <StatsPie title="Deer Seen by Moon Phase" data={moonBreakdown.map((m) => ({ name: m.moon_phase, deer_seen: m.deer_seen }))} nameKey="name" valueKey="deer_seen" loading={statsLoading} />
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          ) : (
            <div className="club-detail-section mb-4">
              <div className="club-detail-section__kicker">
                <GiDeerTrack aria-hidden />
                <span>Deer sightings over time</span>
              </div>
              <div className="club-stats-panel__chart-well">{chartContent}</div>
            </div>
          )}
        </div>
      ) : (
        <Card>
          <Card.Header className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">
              <GiDeerTrack className="me-2" />
              Club Hunt Statistics
            </h5>
          </Card.Header>
          <Card.Body>
            {statsLoading ? (
              <div className="text-center">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading statistics...</span>
              </div>
              ) : clubStats ? (
                <div>
                <Row>
                  <Col md={3} sm={6} className="mb-3">
                    <div className="text-center">
                      <h4 className="text-primary mb-1">{clubStats.total_hunts}</h4>
                      <small className="text-muted">Total Hunts</small>
                    </div>
                  </Col>
                  <Col md={3} sm={6} className="mb-3">
                    <div className="text-center">
                      <h4 className="text-success mb-1">{clubStats.total_harvests}</h4>
                      <small className="text-muted">Harvests</small>
                    </div>
                  </Col>
                  <Col md={3} sm={6} className="mb-3">
                    <div className="text-center">
                      <h4 className="text-warning mb-1">{clubStats.shots_taken}</h4>
                      <small className="text-muted">Shots Taken</small>
                    </div>
                  </Col>
                  <Col md={3} sm={6} className="mb-3">
                    <div className="text-center">
                      <h4 className="text-info mb-1">
                        {Math.round(clubStats.deer_seen_rate || 0)}%
                      </h4>
                      <small className="text-muted">Deer Seen Rate</small>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={4}>
                    <div className="text-center">
                      <h5 className="text-success">{clubStats.deer_sightings.does}</h5>
                      <small className="text-muted">Does Seen</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <h5 className="text-warning">{clubStats.deer_sightings.bucks}</h5>
                      <small className="text-muted">Bucks Seen</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <h5 className="text-info">{clubStats.deer_sightings.fawns}</h5>
                      <small className="text-muted">Fawns Seen</small>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6} className="mb-3">
                    <StatsPie title="Deer Seen by Time of Day" data={timeBreakdown} nameKey="time_period" valueKey="deer_seen" loading={statsLoading} />
                  </Col>
                  <Col md={6} className="mb-3">
                    <StatsPie title="Deer Seen by Weather" data={weatherBreakdown} nameKey="weather" valueKey="deer_seen" loading={statsLoading} />
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col md={6} className="mb-3">
                    <StatsPie title="Deer Seen by Temperature" data={tempBreakdown.map((t) => ({ name: t.label || t.bucket, deer_seen: t.deer_seen }))} nameKey="name" valueKey="deer_seen" loading={statsLoading} />
                  </Col>
                  <Col md={6} className="mb-3">
                    <StatsPie title="Deer Seen by Moon Phase" data={moonBreakdown.map((m) => ({ name: m.moon_phase, deer_seen: m.deer_seen }))} nameKey="name" valueKey="deer_seen" loading={statsLoading} />
                  </Col>
                </Row>

                <div className="mb-4">
                  <h6>Hunt logs</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Hunts</th>
                          <th>Harvests</th>
                          <th>Deer Seen Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...clubStats.member_stats].sort((a, b) => {
                          const aLast = (a.last_name || '').toLowerCase();
                          const bLast = (b.last_name || '').toLowerCase();
                          if (aLast !== bLast) return aLast.localeCompare(bLast);
                          const aFirst = (a.first_name || '').toLowerCase();
                          const bFirst = (b.first_name || '').toLowerCase();
                          if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);
                          return (a.username || '').toLowerCase().localeCompare((b.username || '').toLowerCase());
                        }).map((member, index) => (
                          <tr key={index}>
                            <td>
                              {member.first_name && member.last_name 
                                ? `${member.first_name} ${member.last_name}`
                                : member.username
                              }
                            </td>
                            <td>{member.total_hunts}</td>
                            <td>
                              <Badge bg={member.harvests > 0 ? 'success' : 'secondary'}>
                                {member.harvests}
                              </Badge>
                            </td>
                            <td>
                              <span className={(member.deer_seen_rate || 0) > 0 ? 'text-success' : 'text-muted'}>
                                {Math.round(member.deer_seen_rate || 0)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {chartContent}
              </div>
            ) : (
              <div className="text-center text-muted">
                <p>No hunt statistics available yet.</p>
                <small>Statistics will appear once members start logging hunts.</small>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </Col>
  );
});

export function ClubDetailPage({
  showFeatureList,
  onCloseFeatureList,
  onShowFeatureList,
  initialClub = null,
  mapMaximized = false,
  onToggleMapMaximize = null,
}) {
  const { show: showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isV2 = true;
  const { clubId, threadId } = useParams();
  const { currentUser } = useCurrentUser();
  const { unreadForClub } = useClubBoardUnread({ enabled: Boolean(clubId) });
  const boardUnreadCount = unreadForClub(clubId);
  const [club, setClub] = useState(() => initialClub || getCachedClub(clubId) || null);
  const [loading, setLoading] = useState(() => !(initialClub || getCachedClub(clubId)));
  const [error, setError] = useState('');
  // Stats hydrate from cache once the season is known; the key includes it.
  const [clubStats, setClubStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [clubSeasons, setClubSeasons] = useState(null);
  const [season, setSeason] = useState('');
  const [seasonsResolved, setSeasonsResolved] = useState(false);
  const [huntersInWoods, setHuntersInWoods] = useState([]);
  const [standLogSheet, setStandLogSheet] = useState(null);
  const [standLogLoading, setStandLogLoading] = useState(false);
  const [standLogError, setStandLogError] = useState(null);
  const [clubWeather, setClubWeather] = useState(null);
  const [clubWeatherLoading, setClubWeatherLoading] = useState(false);
  const [clubWeatherError, setClubWeatherError] = useState(null);
  const [clubHarvests, setClubHarvests] = useState([]);
  // Deer log pagination (independent of hunts)
  const [deerLimit] = useState(5);
  const [deerOffset, setDeerOffset] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Hunt log state
  const [hunts, setHunts] = useState([]);
  const [huntsCount, setHuntsCount] = useState(0);
  const [huntsLimit, setHuntsLimit] = useState(5);
  const [huntsOffset, setHuntsOffset] = useState(0);
  const [huntsLoading, setHuntsLoading] = useState(false);
  const [allHunts, setAllHunts] = useState([]);
  const [allHuntsLoaded, setAllHuntsLoaded] = useState(false);
  const [huntsAllLoading, setHuntsAllLoading] = useState(false);
  const [huntSearchOffset, setHuntSearchOffset] = useState(0);
  const [huntSearch, setHuntSearch] = useState('');
  const [deerSearch, setDeerSearch] = useState('');
  const [memberRosterSearch, setMemberRosterSearch] = useState('');
  const [inviteMemberOpen, setInviteMemberOpen] = useState(false);
  const tabParam = searchParams.get('tab');
  const joinMode = searchParams.get('join') === '1';
  const planIdRaw = Number(searchParams.get('planId'));
  const initialOpenPlanId = Number.isFinite(planIdRaw) && planIdRaw > 0 ? planIdRaw : null;
  const [joinGateActive, setJoinGateActive] = useState(false);
  const isMobileClubShell = useIsMobileMd();
  const pathActiveTab = (() => {
    const path = location.pathname;
    if (path.endsWith('/map')) return 'map';
    if (path.endsWith('/calendar')) return 'calendar';
    if (path.endsWith('/rules')) return 'rules';
    if (path.endsWith('/board') || path.includes('/board/')) return 'board';
    return null;
  })();
  const { mode: clubSectionMode, tab: resolvedTab } = resolveClubDetailSectionMode({
    isMobile: isMobileClubShell,
    tabParam,
    pathActiveTab,
  });
  const activeTab = resolvedTab;
  const showMobileHub = clubSectionMode === 'hub';
  const displayTab = showMobileHub
    ? null
    : joinGateActive && activeTab !== 'rules'
      ? 'rules'
      : activeTab;
  const mapExpanded = Boolean(mapMaximized) && displayTab === 'map';
  const showClubNameTopbar = shouldShowClubNameTopbar({
    mapExpanded,
    isMobile: isMobileClubShell,
    displayTab,
  });

  useEffect(() => {
    if (!clubId || !club) return undefined;
    if (club.is_president) {
      setJoinGateActive(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get(`/clubs/${clubId}/rules/`);
        if (cancelled) return;
        setJoinGateActive(
          isClubJoinGateActive({
            isPresident: false,
            acknowledged: resp?.data?.acknowledged,
          })
        );
      } catch {
        // Non-members (403) or load errors — do not lock the shell.
        if (!cancelled) setJoinGateActive(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId, club]);

  useEffect(() => {
    if (!joinGateActive || !clubId) return;
    if (isMobileClubShell) {
      const explicit = tabParam || pathActiveTab;
      // Hub stays visible with locked tiles; only redirect when already drilled into a non-rules tab.
      if (explicit && explicit !== 'rules') {
        navigate(clubJoinGateRulesPath(clubId, { joinMode }), { replace: true });
      }
      return;
    }
    if (!isClubRulesPath(location.pathname)) {
      navigate(clubJoinGateRulesPath(clubId, { joinMode }), { replace: true });
    }
  }, [
    joinGateActive,
    clubId,
    location.pathname,
    navigate,
    joinMode,
    isMobileClubShell,
    tabParam,
    pathActiveTab,
  ]);

  useEffect(() => {
    if (activeTab !== 'members') {
      setMemberRosterSearch('');
      setInviteMemberOpen(false);
    }
  }, [activeTab]);

  const membersById = useMemo(() => {
    const map = {};
    (club?.members || []).forEach((m) => {
      if (m?.id == null) return;
      map[m.id] = m;
      map[String(m.id)] = m;
    });
    return map;
  }, [club]);
  const profileIds = useMemo(() => collectProfileIds(club), [club]);
  const seedProfiles = useMemo(() => profilesFromClub(club), [club]);
  const profilesById = useMemberProfiles(profileIds, seedProfiles);
  const huntsSource = useMemo(() => {
    if (huntSearch) {
      return allHuntsLoaded ? allHunts : hunts;
    }
    return hunts;
  }, [huntSearch, allHuntsLoaded, allHunts, hunts]);
  const filteredHunts = useMemo(
    () =>
      searchItems(huntsSource, huntSearch, {
        fields: [
          (h) => h.date,
          (h) => h.user?.username,
          (h) => h.location,
          (h) => h.total_deer_seen,
          (h) => (h.harvest ? 'harvested' : '')
        ]
      }),
    [huntsSource, huntSearch]
  );
  const filteredHarvests = useMemo(
    () =>
      searchItems(clubHarvests, deerSearch, {
        fields: [
          (h) => h.date,
          (h) => h.hunter_first_name,
          (h) => h.hunter_last_name,
          (h) => h.hunter_username,
          (h) => h.location,
          (h) => h.deer_type,
          (h) => h.weight,
          (h) => h.points
        ]
      }),
    [clubHarvests, deerSearch]
  );

  useEffect(() => {
    setDeerOffset(0);
  }, [deerSearch]);

  // Rules handled on dedicated page /clubs/:id/rules
  const clubStatsLoadedRef = useRef(false);

  useEffect(() => {
    clubStatsLoadedRef.current = false;
    setClubStats(null);
    setClubHarvests([]);
    if (!initialClub) {
      const cached = getCachedClub(clubId);
      setClub(cached);
      setLoading(!cached);
    }
  }, [clubId, initialClub]);

  const rememberClub = useCallback((next) => {
    setClub(next);
    if (next) setCachedClub(next);
  }, []);

  const loadClub = useCallback(async (force = false) => {
    try {
      const cached = getCachedClub(clubId);
      if (cached) {
        setClub(cached);
        setLoading(false);
      }
      if (!cached) setLoading(true);
      const response = await fetchClub(api, clubId, { force });
      if (response?.data) setClub(response.data);
    } catch (error) {
      const fallback = getCachedClub(clubId);
      if (fallback) {
        setClub(fallback);
      } else {
        setError('Failed to load club details');
      }
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  const loadClubStats = useCallback(async (force = false) => {
    try {
      const cacheKey = clubStatsKey(clubId, season);
      const cached = getCachedStats(cacheKey);
      if (cached) {
        setClubStats(cached);
      }
      if (!force && clubStatsLoadedRef.current && isCachedStatsFresh(cacheKey)) {
        return;
      }
      if (!force && isCachedStatsFresh(cacheKey)) {
        clubStatsLoadedRef.current = true;
        return;
      }
      setStatsLoading(true);
      const response = await api.get(`/clubs/${clubId}/hunt_stats/${seasonQuery(season)}`);
      setClubStats(response.data);
      setCachedStats(cacheKey, response.data);
      clubStatsLoadedRef.current = true;
    } catch (error) {
    } finally {
      setStatsLoading(false);
    }
  }, [clubId, season]);

  const loadClubHarvests = useCallback(async (force = false) => {
    try {
      const cached = getCachedClubHarvests(clubId, season);
      if (cached) {
        setClubHarvests(cached);
      }
      if (!force && isCachedClubHarvestsFresh(clubId, season)) {
        return;
      }
      const resp = await api.get(`/clubs/${clubId}/harvests/${seasonQuery(season)}`);
      const rows = normalizeHuntsList(resp.data) || [];
      setClubHarvests(rows);
      setCachedClubHarvests(clubId, season, rows);
      setDeerOffset(0);
    } catch (e) {}
  }, [clubId, season]);

  const loadClubHunts = useCallback(async (limit, offset) => {
    try {
      setHuntsLoading(true);
      const resp = await api.get(`/clubs/${clubId}/hunts/${huntsQuery(limit, offset, season)}`);
      const data = resp.data || {};
      setHunts(data.results || []);
      setHuntsCount(data.count || 0);
      setHuntsLimit(data.limit || limit);
      setHuntsOffset(data.offset || offset);
    } catch (e) {
      // no-op
    } finally {
      setHuntsLoading(false);
    }
  }, [clubId, season]);

  const loadAllHunts = useCallback(async () => {
    if (!clubId) return;
    if (huntsAllLoading || allHuntsLoaded) return;
    try {
      setHuntsAllLoading(true);
      const pageLimit = 200;
      let offset = 0;
      let collected = [];
      let total = null;
      // Paginate until we have all records
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const resp = await api.get(`/clubs/${clubId}/hunts/${huntsQuery(pageLimit, offset, season)}`);
        const data = resp.data || {};
        const results = data.results || [];
        total = data.count != null ? data.count : total;
        collected = collected.concat(results);
        if (results.length < pageLimit) break;
        offset += pageLimit;
        if (total != null && collected.length >= total) break;
      }
      setAllHunts(collected);
      setAllHuntsLoaded(true);
    } catch (e) {
      // ignore fetch-all errors to avoid breaking page pagination
    } finally {
      setHuntsAllLoading(false);
    }
  }, [clubId, huntsAllLoading, allHuntsLoaded, season]);

  useEffect(() => {
    setHuntSearchOffset(0);
    if (huntSearch && !allHuntsLoaded && !huntsAllLoading) {
      loadAllHunts();
    }
  }, [huntSearch, allHuntsLoaded, huntsAllLoading, loadAllHunts]);

  // Initial loads that should NOT re-run on hunts pagination or season change
  useEffect(() => {
    if (clubId) {
      if (!initialClub) {
        loadClub(true);
      } else {
        setLoading(false);
      }
    }
  }, [clubId, initialClub, loadClub]);

  // Which seasons this club has, and which one to show first. The backend owns
  // the rollover rule, so stats wait for this rather than guessing a window and
  // then correcting itself on screen.
  useEffect(() => {
    if (!clubId) return undefined;
    let cancelled = false;
    setClubSeasons(null);
    setSeason('');
    setSeasonsResolved(false);
    (async () => {
      try {
        const payload = await fetchClubSeasons(clubId);
        if (cancelled) return;
        if (payload) {
          setClubSeasons(payload);
          setSeason(initialSeason(payload));
        }
      } catch (e) {
        // Season filtering is additive: without it the endpoints keep their
        // legacy windows, so a failure here degrades rather than breaks.
      } finally {
        if (!cancelled) setSeasonsResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  useEffect(() => {
    if (clubId && seasonsResolved) {
      loadClubStats();
      loadClubHarvests();
    }
  }, [clubId, seasonsResolved, loadClubStats, loadClubHarvests]);

  useEffect(() => {
    setAllHunts([]);
    setAllHuntsLoaded(false);
    setHuntSearchOffset(0);
    setHuntSearch('');
    setHuntsAllLoading(false);
  }, [clubId]);

  // Load hunts when pagination changes
  useEffect(() => {
    if (clubId && seasonsResolved) {
      loadClubHunts(huntsLimit, huntsOffset);
    }
  }, [clubId, seasonsResolved, huntsLimit, huntsOffset, loadClubHunts]);

  // Avoid global focus reloads; navigation already fetches once.

  // Delay initial load; load on search input

  useEffect(() => {
    if (club) {
      const hunters = getHuntersInWoods(club);
      setHuntersInWoods(hunters);
    }
  }, [club]);

  // Fetch current weather at club boundary centroid for In The Woods card (max once per 24h per club to save API calls)
  useEffect(() => {
    if (!club?.id || !club?.map?.hunt_boundaries?.length || !weatherService.isWeatherConfigured()) {
      setClubWeather(null);
      setClubWeatherError(null);
      return;
    }
    const cacheKey = `yona_club_weather_${club.id}`;
    const cacheTtlMs = 24 * 60 * 60 * 1000;
    try {
      const cached = window.localStorage?.getItem(cacheKey);
      if (cached) {
        const { timestamp, data } = JSON.parse(cached);
        if (data && typeof timestamp === 'number' && (Date.now() - timestamp) < cacheTtlMs) {
          setClubWeather(data);
          setClubWeatherError(null);
          return;
        }
      }
    } catch (_) { /* ignore invalid cache */ }
    const centroid = getCentroidFromBoundaries(club.map.hunt_boundaries);
    if (!centroid) return;
    setClubWeatherLoading(true);
    setClubWeatherError(null);
    weatherService.getCurrentWeather(centroid.lat, centroid.lng, null)
      .then((data) => {
        setClubWeather(data);
        try {
          window.localStorage?.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data }));
        } catch (_) { /* ignore quota etc */ }
      })
      .catch((err) => setClubWeatherError(err?.message ?? 'Weather unavailable'))
      .finally(() => setClubWeatherLoading(false));
  }, [club?.id, club?.map?.hunt_boundaries]);

  // Subscribe to hunt completion events to refresh club data and In The Woods
  useEffect(() => {
    const unsubscribe = onHuntCompleted(async () => {
      try {
        await loadClub(true);
        await loadClubStats(true);
        await loadClubHunts(huntsLimit, huntsOffset);
        await loadClubHarvests(true);
      } catch (_) {}
    });
    return () => {
      try { unsubscribe && unsubscribe(); } catch (_) {}
    };
  }, [loadClub, loadClubStats, loadClubHunts, loadClubHarvests, huntsLimit, huntsOffset]);

  // Consume any pending hunt completion on mount
  useEffect(() => {
    const pending = consumePendingHuntCompleted();
    if (pending) {
      (async () => {
        try {
          await loadClub(true);
          await loadClubStats(true);
          await loadClubHunts(huntsLimit, huntsOffset);
          await loadClubHarvests(true);
        } catch (_) {}
      })();
    }
    // no cleanup
  }, [loadClub, loadClubStats, loadClubHunts, loadClubHarvests, huntsLimit, huntsOffset]);


  const handleRemoveMember = useCallback(async (userId) => {
    if (!club?.is_creator) return;
    try {
      await api.post(`/clubs/${club.id}/remove_member/`, { user_id: userId });
      await loadClub(true);
      setError('');
    } catch (error) {
      setError('Failed to remove member. Please try again.');
    }
  }, [club, loadClub]);

  const handleMessageMember = useCallback((member) => {
    NavigationService.navigateToUserMessage(member.id, member.username, navigate);
  }, [navigate]);

  const handleInviteMemberSubmit = useCallback(({ email } = {}) => {
    try {
      showToast({
        title: 'Coming soon',
        body: email
          ? `Invitations by email aren’t enabled yet (${email}). Invite an existing Yona user from the directory instead.`
          : 'Invitations by email aren’t enabled yet. Invite an existing Yona user from the directory instead.',
        variant: 'info',
      });
    } catch (_) { /* no-op */ }
  }, [showToast]);

  const requestEndHunt = useCallback(async (hunter) => {
    try {
      await messagingService.sendEndHuntRequest(hunter.userId, clubId, hunter.locationName);
      try { showToast({ title: 'Request sent', body: 'End Hunt request sent successfully.', variant: 'success' }); } catch (e) {}
    } catch (error) {
      try { showToast({ title: 'Send failed', body: 'Failed to send End Hunt request. Please try again.', variant: 'danger' }); } catch (e) {}
    }
  }, [clubId, showToast]);

  const endHunt = useCallback((hunter) => {
    if (!currentUser?.id) return;
    const hunterUserId = parseInt(hunter.userId, 10);
    const currentUserId = parseInt(currentUser.id, 10);
    if (hunterUserId !== currentUserId) {
      alert(`You can only end your own hunts. This hunt belongs to ${hunter.userName}.`);
      return;
    }
    const loc =
      (club?.hunt_locations || []).find(
        (l) => Number(l.id) === Number(hunter.locationId)
      ) || null;
    const sheetLocation = {
      id: `location-${hunter.locationId}`,
      name: hunter.locationName || loc?.name || 'Hunt Location',
      notes: loc?.notes || '',
      optimal_wind: loc?.optimal_wind,
      optimal_winds: loc?.optimal_winds,
      geojson: loc?.geojson || null,
      location: loc || { id: hunter.locationId, name: hunter.locationName },
      properties: {
        id: `location-${hunter.locationId}`,
        name: hunter.locationName || loc?.name,
        featureType: 'hunt_location',
        isHuntLocation: true,
        isOccupied: true,
      },
    };
    setStandLogError(null);
    setStandLogSheet({
      location: sheetLocation,
      occupancy: {
        isOccupied: true,
        isMine: true,
        occupiedBy: hunter.userName,
        occupiedByUserId: hunter.userId,
        timePeriod: hunter.timePeriod,
        date: toLocalYMD(new Date()),
      },
    });
  }, [currentUser?.id, club?.hunt_locations]);

  const handleStandLogSignIn = useCallback(async (locationId, date, timePeriod, options = {}) => {
    setStandLogLoading(true);
    setStandLogError(null);
    try {
      await HuntSignOutService.signInLocation(locationId, date, timePeriod, {
        userId: options.userId,
        requireRemoved: options.requireRemoved,
      });
      try { notifyInTheStandChanged(); } catch (_) {}
    } catch (err) {
      setStandLogError(err.message || 'Failed to clear stand');
      throw err;
    } finally {
      setStandLogLoading(false);
    }
  }, []);

  const handleStandLogClose = useCallback(() => {
    setStandLogSheet(null);
    setStandLogError(null);
    setStandLogLoading(false);
    // Refresh In the woods after give-up or completed log stamp dismiss
    try {
      loadClub(true);
    } catch (_) {}
  }, [loadClub]);

  const onViewMap = useCallback(() => navigate(clubMapTabPath(club.id)), [navigate, club]);
  const onDrawBoundary = useCallback(
    () => navigate(clubMapDrawBoundaryPath(club.id)),
    [navigate, club]
  );
  const refreshClubStats = useCallback(() => loadClubStats(true), [loadClubStats]);
  /**
   * Season scopes the hunt log as well as the tiles, so paging and the
   * search-all cache reset in the same batch as the season itself — doing it in
   * a reactive effect would fire one request for the old page first.
   */
  const handleSeasonChange = useCallback((next) => {
    setSeason(next);
    setHuntsOffset(0);
    setDeerOffset(0);
    setAllHunts([]);
    setAllHuntsLoaded(false);
    setHuntSearchOffset(0);
    setHuntSearch('');
    setHuntsAllLoading(false);
  }, []);
  const handleTabSelect = useCallback((nextTab) => {
    if (joinGateActive && nextTab !== 'rules') return;
    const base = `/clubs/${clubId}`;
    switch (nextTab) {
      case 'map':
        // Stay in club detail shell (handoff Map tab) — not fullscreen /map
        setSearchParams({ tab: 'map' });
        if (location.pathname !== base) {
          navigate(`${base}?tab=map`);
        }
        return;
      case 'calendar':
        navigate(`${base}/calendar`);
        return;
      case 'rules':
        navigate(clubJoinGateRulesPath(clubId, { joinMode }));
        return;
      case 'board':
        navigate(`${base}/board`);
        return;
      case 'overview':
        // Mobile: ?tab=overview keeps the section drilled-in (bare /clubs/:id is the hub).
        if (isMobileClubShell) {
          setSearchParams({ tab: 'overview' });
          if (location.pathname !== base) {
            navigate(`${base}?tab=overview`);
          }
        } else {
          setSearchParams({});
          if (location.pathname !== base) {
            navigate(base);
          }
        }
        return;
      default:
        setSearchParams({ tab: nextTab });
        if (location.pathname !== base) {
          navigate(`${base}?tab=${nextTab}`);
        }
    }
  }, [clubId, navigate, setSearchParams, location.pathname, joinGateActive, joinMode, isMobileClubShell]);

  const handleHubBack = useCallback(() => {
    const base = `/clubs/${clubId}`;
    setSearchParams({});
    if (location.pathname !== base) {
      navigate(base);
    }
  }, [clubId, location.pathname, navigate, setSearchParams]);
  // rules save/ack handled on Rules page

  const onImageClick = useCallback((idx) => {
    setLightboxIndex(idx);
    setShowLightbox(true);
  }, []);

  const lightboxItems = useMemo(() => {
    if (!showLightbox) return [];
    return (clubHarvests || []).map(h => ({
      caption: `${(h.hunter_first_name && h.hunter_last_name) ? `${h.hunter_first_name} ${h.hunter_last_name}` : (h.hunter_username || 'Member')} · ${h.location || ''}`,
      url: h.photo_url,
      getUrl: async () => h.photo_url,
    }));
  }, [showLightbox, clubHarvests]);

  // Non-member listing handled by backend search; no local filter needed


  const huntsDisplayRows = huntSearch
    ? filteredHunts.slice(huntSearchOffset, huntSearchOffset + huntsLimit)
    : hunts;
  const huntsDisplayCount = huntSearch ? filteredHunts.length : huntsCount;
  const huntsDisplayOffset = huntSearch ? huntSearchOffset : huntsOffset;

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading club details...</p>
        </div>
      </Container>
    );
  }

  if (!club) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <h4>Club Not Found</h4>
          <p className="text-muted">The club you're looking for doesn't exist or you don't have access to it.</p>
          <Button variant="primary" onClick={() => navigate('/clubs')}>
            <FaArrowLeft className="me-2" />
            Back to Clubs
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div className={`club-detail-root${mapExpanded ? ' club-detail-root--map-maximized' : ''}`}>
      <Container fluid className={`club-detail-page px-0${mapExpanded ? ' club-detail-page--map-maximized' : ''}`}>
        {showClubNameTopbar ? (
        <header className="club-detail-topbar" data-testid="club-detail-topbar">
          <div className="club-detail-topbar__inner">
            <div className="club-detail-topbar__start">
              {isV2 ? (
                <YButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="club-detail-back-btn"
                  aria-label="Back to clubs"
                  onClick={() => navigate('/clubs')}
                >
                  <FaArrowLeft aria-hidden />
                </YButton>
              ) : (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="club-detail-back-btn d-inline-flex align-items-center justify-content-center"
                  style={{ width: 36, height: 36, padding: 0 }}
                  aria-label="Back to clubs"
                  onClick={() => navigate('/clubs')}
                >
                  <FaArrowLeft aria-hidden />
                </Button>
              )}
              {club.logo_url ? (
                <div className="club-detail-topbar__logo">
                  <CachedImage src={club.logo_url} alt="" />
                </div>
              ) : (
                <div
                  className="club-detail-topbar__avatar"
                  aria-hidden="true"
                  data-testid="club-detail-avatar"
                >
                  {getClubInitials(club.name)}
                </div>
              )}
            </div>
            <div className="club-detail-topbar__main">
              <div className="club-detail-topbar__title-row">
                <h1 className="club-detail-topbar__title">{club.name}</h1>
              </div>
              <p className="club-detail-topbar__location" data-testid="club-detail-shell-meta">
                <span>{formatClubDetailShellMeta(club)}</span>
              </p>
            </div>
            <div className="club-detail-topbar__stats">
              {club.is_president ? (
                <div className="club-detail-pill club-detail-pill--admin" role="group" aria-label="Administrator access">
                  <FaCrown aria-hidden />
                  <span className="club-detail-pill__admin-label">ADMIN</span>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        ) : null}

        {!mapExpanded && (
        <>
        <div className="club-detail-tabs-bar d-none d-md-block" data-testid="club-detail-tabs-bar">
          <ClubTabsNav
            isPresident={club.is_president}
            activeKey={displayTab || 'overview'}
            onSelect={handleTabSelect}
            boardUnreadCount={boardUnreadCount}
            joinGateActive={joinGateActive}
          />
        </div>

        {showMobileHub ? (
          <ClubTileHub
            isPresident={Boolean(club.is_president)}
            joinGateActive={joinGateActive}
            boardUnreadCount={boardUnreadCount}
            memberCount={club.member_count || club.members?.length || 0}
            onSelect={handleTabSelect}
          />
        ) : null}

        {isMobileClubShell && displayTab ? (
          <ClubHubBackBar sectionKey={displayTab} onBack={handleHubBack} />
        ) : null}
        </>
        )}

        <div className="club-detail-body">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
            {error}
          </Alert>
        )}

        {displayTab === 'overview' && (
          <Row className="club-overview-top-row g-4">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="club-detail-section" data-testid="club-detail-club-info-panel">
                <div className="club-detail-section__kicker">
                  <FaInfoCircle aria-hidden />
                  <span>Club Info</span>
                </div>
                {club.description ? (
                  <p className="club-detail-about-lead">{club.description}</p>
                ) : null}
                <div className="club-detail-kv-stack">
                  {[
                    { Icon: FaFlag, label: 'Name', value: club.name },
                    { Icon: FaCalendarAlt, label: 'Established', value: club.established_year != null && club.established_year !== '' ? String(club.established_year) : '—' },
                    {
                      Icon: FaTree,
                      label: 'Acreage',
                      value: club.acreage != null ? `${Number(club.acreage).toLocaleString()} ac` : 'Boundary not set'
                    },
                    ...huntZoneOverviewRows(club).map((row) => ({
                      Icon: row.label === 'Season' ? FaCalendarAlt : GiDeerTrack,
                      label: row.label,
                      value: row.value,
                    })),
                    {
                      Icon: FaUsers,
                      label: 'Members',
                      value: String(club.member_count || (club.members?.length ?? 0))
                    }
                  ].map(({ Icon, label, value }) => (
                    <div key={label} className="club-detail-kv-row" data-testid={`club-detail-kv-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                      <span className="club-detail-kv-row__label">
                        <Icon aria-hidden />
                        {label}
                      </span>
                      <span className="club-detail-kv-row__val">{value}</span>
                    </div>
                  ))}
                </div>
                <div className="club-detail-officers-block">
                  <div className="club-detail-section__subhead">Officers</div>
                  {Array.isArray(club.officers) && club.officers.length > 0 ? (
                    <div className="d-grid gap-2">
                      {club.officers.map((off, idx) => {
                          const title = off.title || 'Officer';
                          const label = off.name || '—';
                          const member = off.user_id
                            ? (membersById[off.user_id] || membersById[String(off.user_id)])
                            : matchOfficerToMember(off, club.members || []);
                          const prof = member
                            ? (profilesById[member.id] || profilesById[off.user_id])
                            : (off.user_id ? profilesById[off.user_id] : null);
                          const photoUrl = prof?.photo_thumbnail_url
                            || prof?.photo_url
                            || member?.profile?.photo_thumbnail_url
                            || member?.profile?.photo_url
                            || member?.photo_url;
                          const displayName = member
                            ? (
                              (prof && [prof.first_name, prof.last_name].filter(Boolean).join(' ').trim())
                              || [member.profile?.first_name, member.profile?.last_name].filter(Boolean).join(' ').trim()
                              || member.username
                              || label
                            )
                            : label;
                          const initials = (member?.profile?.first_name && member?.profile?.last_name)
                            ? `${member.profile.first_name[0]}${member.profile.last_name[0]}`.toUpperCase()
                            : (displayName || 'O').substring(0, 2).toUpperCase();
                          return (
                            <div key={`${title || 'officer'}-${idx}`} className="club-detail-officer-row">
                              <div className="d-flex align-items-center gap-2 min-w-0">
                                {photoUrl ? (
                                  <CachedImage src={photoUrl} alt={displayName} className="nav-avatar" style={{ width: 32, height: 32 }} />
                                ) : (
                                  <div className="nav-avatar-fallback" title={displayName} style={{ width: 32, height: 32, fontSize: 12 }}>
                                    {initials}
                                  </div>
                                )}
                                <div className="d-flex flex-column min-w-0">
                                  <span className="fw-semibold">{title}</span>
                                  {member ? (
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-start text-body"
                                      onClick={() => navigate(`/users/${member.id}`)}
                                    >
                                      {displayName}
                                    </button>
                                  ) : (
                                    <span className="text-muted">{displayName}</span>
                                  )}
                                </div>
                              </div>
                              {member && (
                                <YButton
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleMessageMember(member)}
                                  title={`Message ${member.username || displayName}`}
                                  className="ms-2 flex-shrink-0"
                                >
                                  <FaEnvelope />
                                </YButton>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-muted small">No officers listed</div>
                  )}
                </div>
                <ClubFeedersSummaryPanel clubId={clubId} />
                <ClubSharedPlansPanel
                  clubId={clubId}
                  clubName={club?.name || ''}
                  initialOpenPlanId={initialOpenPlanId}
                />
              </div>
            </Col>

            <Col lg={6} className="mb-4">
              <InTheWoodsPanel
                  isV2={isV2}
                  huntersInWoods={huntersInWoods}
                  currentUserId={currentUser?.id}
                  clubIsCreator={club.is_creator}
                  onEndHunt={endHunt}
                  onRequestEndHunt={requestEndHunt}
                  canViewMap={Boolean(club?.map && Array.isArray(club.map.hunt_boundaries) && club.map.hunt_boundaries.length > 0)}
                  canDrawBoundary={Boolean(club?.is_president && !clubHasMapBoundary(club))}
                  onViewMap={onViewMap}
                  onDrawBoundary={onDrawBoundary}
                  clubWeather={clubWeather}
                  clubWeatherLoading={clubWeatherLoading}
                  clubWeatherError={clubWeatherError}
                  showClubWeather={Boolean(clubHasMapBoundary(club) && weatherService.isWeatherConfigured())}
                  clubStats={clubStats}
                  statsLoading={statsLoading}
                />
            </Col>
          </Row>
        )}

        {displayTab === 'map' && (
          <div className="club-detail-section club-detail-section--map" data-testid="club-detail-map-panel">
            <UnifiedMapPage
              showFeatureList={showFeatureList}
              onCloseFeatureList={onCloseFeatureList}
              onShowFeatureList={onShowFeatureList}
              embedStandBoard
              mapMaximized={mapExpanded}
              onToggleMapMaximize={onToggleMapMaximize}
            />
          </div>
        )}

        {displayTab === 'members' && (
          <Row>
            <Col lg={12} className="mb-4">
              <div className="club-detail-section club-detail-section--members" data-testid="club-detail-members-panel">
                <div className="club-members-stats" data-testid="club-members-stats">
                  <div className="club-members-stat">
                    <div className="club-members-stat__label">Members</div>
                    <div className="club-members-stat__value">
                      {(club.member_count ?? club.members?.length ?? 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="club-members-stat club-members-stat--woods">
                    <div className="club-members-stat__label">In the woods</div>
                    <div className="club-members-stat__value">{huntersInWoods.length}</div>
                  </div>
                </div>
                <div className="club-members-toolbar">
                  <div className="club-members-search">
                    <FaSearch className="club-members-search__icon" aria-hidden />
                    <YInput
                      type="search"
                      className="club-members-search__input"
                      placeholder="Search the roster…"
                      aria-label="Search members on this page"
                      value={memberRosterSearch}
                      onChange={(e) => setMemberRosterSearch(e.target.value)}
                    />
                  </div>
                  {club.is_president ? (
                    <div className="club-members-toolbar__actions">
                      <YButton
                        type="button"
                        variant="primary"
                        className="club-members-invite-btn"
                        onClick={() => setInviteMemberOpen(true)}
                      >
                        <FaUserPlus aria-hidden />
                        Invite member
                      </YButton>
                    </div>
                  ) : null}
                </div>
                {club.is_president && (
                  <div className="club-members-directory-block">
                    <div className="club-detail-section__subhead">Invite from directory</div>
                    <AddMemberAccordion
                      clubId={club.id}
                      existingMembers={club.members || []}
                      className="mb-0"
                    />
                  </div>
                )}
                <MembersInlineListV2
                  club={club}
                  onMessage={handleMessageMember}
                  onRemove={handleRemoveMember}
                  initialCount={10}
                  profilesById={profilesById}
                  searchQuery={memberRosterSearch}
                />
              </div>
            </Col>
          </Row>
        )}

        {displayTab === 'stats' && (
          <>
            <Row>
              <Col lg={12} className="mb-4">
                <ClubStatsSeasonTiles
                  clubStats={clubStats}
                  statsLoading={statsLoading}
                  onRefresh={refreshClubStats}
                  seasons={clubSeasons}
                  season={season}
                  onSeasonChange={handleSeasonChange}
                />
              </Col>
            </Row>
            <ClubStatsPanel clubStats={clubStats} statsLoading={statsLoading} club={club} />
            <Row>
              <Col lg={12} className="mb-4">
                <DeerLogTable
                  isV2={isV2}
                  harvests={filteredHarvests.slice(deerOffset, deerOffset + deerLimit)}
                  onImageClick={onImageClick}
                  limit={deerLimit}
                  offset={deerOffset}
                  count={filteredHarvests.length}
                  onPrev={() => setDeerOffset((o) => Math.max(0, o - deerLimit))}
                  onNext={() => setDeerOffset((o) => (o + deerLimit < filteredHarvests.length ? o + deerLimit : o))}
                  searchTerm={deerSearch}
                  onSearchChange={setDeerSearch}
                />
              </Col>
            </Row>
            <ClubHuntLog
              isV2={isV2}
              hunts={huntsDisplayRows}
              count={huntsDisplayCount}
              limit={huntsLimit}
              offset={huntsDisplayOffset}
              loading={huntsLoading}
              onPrev={() => huntSearch
                ? setHuntSearchOffset((o) => Math.max(0, o - huntsLimit))
                : (huntsOffset > 0 ? loadClubHunts(huntsLimit, Math.max(0, huntsOffset - huntsLimit)) : null)}
              onNext={() => huntSearch
                ? setHuntSearchOffset((o) => ((o + huntsLimit) < huntsDisplayCount ? o + huntsLimit : o))
                : ((huntsOffset + huntsLimit) < huntsCount ? loadClubHunts(huntsLimit, huntsOffset + huntsLimit) : null)}
              searchTerm={huntSearch}
              onSearchChange={setHuntSearch}
              isSearchingAll={Boolean(huntSearch) && huntsAllLoading && !allHuntsLoaded}
            />
          </>
        )}

        {displayTab === 'calendar' && (
          <ClubCalendarPage embedded clubId={clubId} isPresident={!!club?.is_president} club={club} />
        )}

        {displayTab === 'rules' && (
          <Row>
            <Col lg={12} className="mb-4">
              <ClubRulesPage
                embedded
                joinMode={joinMode}
                onJoinGateChange={setJoinGateActive}
                onJoinComplete={() => {
                  setJoinGateActive(false);
                  const next = new URLSearchParams(searchParams);
                  next.delete('join');
                  const qs = next.toString();
                  navigate(qs ? `/clubs/${clubId}?${qs}` : `/clubs/${clubId}`);
                }}
              />
            </Col>
          </Row>
        )}

        {displayTab === 'board' && (
          <Row>
            <Col lg={12} className="mb-4">
              {threadId ? (
                <ClubThreadPage embedded clubId={club?.id} threadId={threadId} />
              ) : (
                <ClubBoardPage
                  embedded
                  clubId={club?.id}
                  isPresident={Boolean(club?.is_president)}
                  clubName={club?.name}
                  memberCount={club?.member_count}
                />
              )}
            </Col>
          </Row>
        )}

        {displayTab === 'admin' && club.is_president && (
          <Row>
            <Col lg={12} className="mb-4">
              <ClubAdminPage embedded clubId={club?.id} onClubUpdated={rememberClub} />
            </Col>
          </Row>
        )}
        </div>
      </Container>
      {showLightbox && (
        <Lightbox
          items={lightboxItems}
          initialIndex={lightboxIndex}
          onClose={() => setShowLightbox(false)}
          rightPanel={<PhotoEngagementPanel harvestId={clubHarvests[lightboxIndex]?.harvest_id} />}
        />
      )}
      {club?.is_president && (
        <InviteMemberModal
          show={inviteMemberOpen}
          onHide={() => setInviteMemberOpen(false)}
          onSubmit={handleInviteMemberSubmit}
        />
      )}
      <SignOutModal
        show={Boolean(standLogSheet)}
        location={standLogSheet?.location || null}
        occupancy={standLogSheet?.occupancy || null}
        clubId={clubId}
        initialStep="saw"
        onSignOut={async () => {}}
        onSignIn={handleStandLogSignIn}
        onClose={handleStandLogClose}
        onStandReleased={() => {
          try {
            showToast({ title: 'Stand cleared', body: 'The stand is open again.', variant: 'success' });
          } catch (_) {}
        }}
        isPresident={Boolean(club?.is_president)}
        clubMembers={club?.members || []}
        currentUser={currentUser}
        loading={standLogLoading}
        error={standLogError}
      />
    </div>
  );
}


// Rules are managed on a dedicated page now.
